// CreditCardItem.jsx
import React from "react";
import { Card as AntCard } from "antd";

const formatCardNumber = (num = "") => {
  const last4 = String(num).slice(-4).padStart(4, "•");
  return `•••• •••• •••• ${last4}`;
};

const maskedCvv = (cvv) => (cvv ? "•••" : "***");

// Metallic realistic gradients
const REAL_GRADIENTS = [
  "linear-gradient(135deg,#001f3f 0%,#0074D9 100%)", // royal navy blue
  "linear-gradient(135deg,#3C1053 0%,#AD5389 100%)", // deep maroon-purple
  "linear-gradient(135deg,#000428 0%,#004e92 100%)", // midnight blue
  "linear-gradient(135deg,#2C3E50 0%,#4CA1AF 100%)", // teal-blue steel
  "linear-gradient(135deg,#434343 0%,#000000 100%)", // black-metal
  "linear-gradient(135deg,#4B0000 0%,#8B0000 100%)", // metallic dark maroon
  "linear-gradient(135deg,#283E51 0%,#485563 100%)", // steel blue
  "linear-gradient(135deg,#232526 0%,#707070 100%)", // titanium
  "linear-gradient(135deg,#141E30 0%,#243B55 100%)", // darker navy gradient
];

const gradientForId = (id) => REAL_GRADIENTS[id % REAL_GRADIENTS.length];

export const CreditCardItem = ({ card, selectedCardId, setSelectedCardId}) => {
  const gradient = gradientForId(card.id);

  return (
    <AntCard
      bordered={false}
      bodyStyle={{ padding: 0, background: "transparent" }}
      style={{
        margin: "16px auto",
        border: "none", // removes white border
        borderRadius: 16,
        maxWidth: 360,
        aspectRatio: "1.58 / 1", // real ratio
        background: gradient,
        boxShadow:
          "0 16px 28px rgba(0,0,0,0.45), inset 0 1px 2px rgba(255,255,255,0.2)", // metallic depth
        color: "#fff",
        overflow: "hidden",
        position: "relative",
        cursor: "pointer",
      }}
        onClick={() => {
          setSelectedCardId && setSelectedCardId(card.id);

      }}
    >
      {/* Glossy overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(120deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.15) 100%)",
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      />

      {/* Card chip */}
      <img
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAMAAAAp4XiDAAAABGdBTUEAALGPC/xhBQAAACBjSFJN
              AAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAB6VBMVEUAAACNcTiVeUKVeUOY
              fEaafEeUeUSYfEWZfEaykleyklaXe0SWekSZZjOYfEWYe0WXfUWXe0WcgEicfkiXe0SVekSXekSW
              ekKYe0a9nF67m12ZfUWUeEaXfESVekOdgEmVeUWWekSniU+VeUKVeUOrjFKYfEWliE6WeESZe0GS
              e0WYfES7ml2Xe0WXeESUeEOWfEWcf0eWfESXe0SXfEWYekSVeUKXfEWxklawkVaZfEWWekOUekOW
              ekSYfESZe0eXekWYfEWZe0WZe0eVeUSWeETAnmDCoWLJpmbxy4P1zoXwyoLIpWbjvXjivnjgu3bf
              u3beunWvkFWxkle/nmDivXiWekTnwXvkwHrCoWOuj1SXe0TEo2TDo2PlwHratnKZfEbQrWvPrWua
              fUfbt3PJp2agg0v0zYX0zYSfgkvKp2frxX7mwHrlv3rsxn/yzIPgvHfduXWXe0XuyIDzzISsjVO1
              lVm0lFitjVPzzIPqxX7duna0lVncuHTLqGjvyIHeuXXxyYGZfUayk1iyk1e2lln1zYTEomO2llrb
              tnOafkjFpGSbfkfZtXLhvHfkv3nqxH3mwXujhU3KqWizlFilh06khk2fgkqsjlPHpWXJp2erjVOh
              g0yWe0SliE+XekShhEvAn2D///+gx8TWAAAARnRSTlMACVCTtsRl7Pv7+vxkBab7pZv5+ZlL/UnU
              /f3SJCVe+Fx39naA9/75XSMh0/3SSkia+pil/KRj7Pr662JPkrbP7OLQ0JFOijI1MwAAAAFiS0dE
              orDd34wAAAAJcEhZcwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IDx2lsiuJAAACLElEQVRIx2Ng
              GAXkAUYmZhZWPICFmYkRVQcbOwenmzse4MbFzc6DpIGXj8PD04sA8PbhF+CFaxEU8iWkAQT8hEVg
              OkTF/InR4eUVICYO1SIhCRMLDAoKDvFDVhUaEhwUFAjjSUlDdMiEhcOEItzdI6OiYxA6YqODIt3d
              I2DcuDBZsBY5eVTr4xMSYcyk5BRUOXkFsBZFJTQnp6alQxgZmVloUkrKYC0qqmji2WE5EEZuWB6a
              lKoKdi35YQUQRkFYPpFaCouKIYzi6EDitJSUlsGY5RWVRGjJLyxNy4ZxqtIqqvOxaVELQwZFZdkI
              JVU1RSiSalAt6rUwUBdWG1CP6pT6gNqwOrgCdQyHNYR5YQFhDXj8MiK1IAeyN6aORiyBjByVTc0F
              qBoKWpqwRCVSgilOaY2OaUPw29qjOzqLvTAchpos47u6EZyYnngUSRwpuTe6D+6qaFQdOPNLRzOM
              1dzhRZyW+CZouHk3dWLXglFcFIflQhj9YWjJGlZcaKAVSvjyPrRQ0oQVKDAQHlYFYUwIm4gqExGm
              BSkutaVQJeomwViTJqPK6OhCy2Q9sQBk8cY0DxjTJw0lAQWK6cOKfgNhpKK7ZMpUeF3jPa28BCET
              amiEqJKM+X1gxvWXpoUjVIVPnwErw71nmpgiqiQGBjNzbgs3j1nus+fMndc+Cwm0T52/oNR9lsdC
              S24ra7Tq1cbWjpXV3sHRCb1idXZ0sGdltXNxRateRwHRAACYHutzk/2I5QAAACV0RVh0ZGF0ZTpj
              cmVhdGUAMjAyMy0wMi0xM1QwODoxNToyOSswMDowMEUnN7UAAAAldEVYdGRhdGU6bW9kaWZ5ADIw
              MjMtMDItMTNUMDg6MTU6MjkrMDA6MDA0eo8JAAAAKHRFWHRkYXRlOnRpbWVzdGFtcAAyMDIzLTAy
              LTEzVDA4OjE1OjI5KzAwOjAwY2+u1gAAAABJRU5ErkJggg=="
        alt="card chip"
        style={{
          width: 50,
          height: 50,
          borderRadius: 6,
          margin: "20px",
          // border: "1px solid rgba(255,223,128,0.8)",
        }}
      />

      <img
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAQAAAC0NkA6AAAABGdBTUEAALGPC/xhBQAAACBjSFJN
              AAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QA/4ePzL8AAAAJcEhZ
              cwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IEzgIwaKTAAADDklEQVRYw+1XS0iUURQ+f5qPyjQf
              lGRFEEFK76koKGxRbWyVVLSOgsCgwjZBJJYuKogSIoOonUK4q3U0WVBWFPZYiIE6kuArG3VGzK/F
              fPeMM/MLt99/NuHdfPd888/57jn3nvsQWWj/VcMlvMMd5KRTogqx9iCdIjUUmcGR9ImUYowyP3xN
              GQJoRLVaZ2DaZf8kyjEJALhI28ELioyiwC+Rc3QZwRYyO/DH51hQgWm6DMIh10KmD4u9O16K49it
              VoPOAmcGAWWOepXIRScAoJZ2Frro8oN+EyTT6lWkkg6msZfMSR35QTJmjU0g15tIGSJ08ZZMJkJk
              HpNZgSkyXosS13TkJpZ62mPIJvOSzC1bp8vRhhCakEk7G9/o4gmZdbpsTcKu0m63FbnBP9Qrc15z
              bkbemfgNDtEOI8NO5L5O9VYyRYgmJayZ9nPaxZrSjW4+F6Uw9yQqIiIZwhp2huQTf6OIvCZyGM6g
              DJBZbyXifJXr7FZjGXsdxADxI7HUJFB6iWvsIhFpkoiIiGTJfjJfiCuJg2ZEspq9EHGVpYgzKqwJ
              qSAOEwuJQ/pxPvE3cYltJCLdxBLiSKKIE5HxJKcTRNeadxfhDiuYw44zVs1dxKwRk/uCxIiQkxKB
              sSctRVAge9g1E15EHE6yRUaJecRxcWlukdRIbGFOSZCMWQA/iWauIP3slREHXPyliqBcrrD71Amz
              Z+rD1Mt2Yr8TZc/UR4/YtFnbijnHi3UrN9vKQ9rPaJf867ZiaqDB+czeKYmd3pNa6fuI75MiC0uX
              XSR5aEMf7s7a6r/PudVXkjFb/SsrCRfROk0Fx6+H1i9kkTGn/E1vEmt1m089fh+RKdQ5O+xNJPUi
              cUIjO0Dm7HwvErEr0YxeibL1StSh37STafE4I7zcBdRq1DiOkdmlTJVnkQTBTS7X1FYyvfO4piaI
              nKbDCDaT2anLudYXCRFsQBgAcIF2/Okwgvz5+Z4tsw118dzruvIvjhTB+HOuWy8UvovEH6beitBK
              xDyxm9MmISKCWrzB7bSlaqGlsf0FC0gMjzTg6GgAAAAldEVYdGRhdGU6Y3JlYXRlADIwMjMtMDIt
              MTNUMDg6MTk6NTYrMDA6MDCjlq7LAAAAJXRFWHRkYXRlOm1vZGlmeQAyMDIzLTAyLTEzVDA4OjE5
              OjU2KzAwOjAw0ssWdwAAACh0RVh0ZGF0ZTp0aW1lc3RhbXAAMjAyMy0wMi0xM1QwODoxOTo1Nisw
              MDowMIXeN6gAAAAASUVORK5CYII="
        alt="contactless"
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          width: 28,
          height: 28,
        }}
      />
      {/* Card number */}
      <div
        style={{
          fontFamily: "'Courier New', monospace",
          fontSize: 22,
          letterSpacing: 3,
          padding: "10px 20px",
          textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
        }}
      >
        {formatCardNumber(card.cardNo)}
      </div>

      {/* Bottom section */}
      <div
        style={{
          position: "absolute",
          bottom: 18,
          left: 20,
          right: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          fontSize: 14,
        }}
      >
        {/* Cardholder & expiry */}
        <div>
          <div style={{ textTransform: "uppercase", fontWeight: 600 }}>
            {card.name || "CARD HOLDER"}
          </div>
          <div style={{ opacity: 0.85, marginTop: 4 }}>
            {card.expireDate || "MM/YY"}
          </div>
        </div>

        {/* CVV */}
        <div style={{ textAlign: "right" }}>
          <div style={{ opacity: 0.7 }}>CVV</div>
          <div style={{ letterSpacing: 2, fontWeight: 700 }}>
            {maskedCvv(card.cvv)}
          </div>
        </div>
      </div>
    </AntCard>
  );
};

export default CreditCardItem;
